import { ElementRef, HostBinding, Input, Renderer2, Directive } from '@angular/core';
import { AbstractControl, NgForm, ValidationErrors, Validator } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { BrowserService } from 'webcommon/legacy-common';
import { ApBaseDirective } from '../ap-base-component/index';

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class ApRequiredBase extends ApBaseDirective implements Validator {
  @HostBinding('attr.name') @Input() name: string;
  private control: AbstractControl;
  private element: Element;
  private formGroupElement: Element | null;
  private formGroupInputElement: Element | null;
  private injectedDiv: Element | null;
  private label: Element | null;

  constructor(
    private browserService: BrowserService,
    private elementRef: ElementRef,
    private ngForm: NgForm,
    private renderer: Renderer2,
  ) {
    super();
  }

  validate(control: AbstractControl): ValidationErrors | null {

    if (!this.control) {
      this.initialize(control);
    }

    return this.getIsValid() ? null : {
      apRequired: true,
    };
  }

  protected abstract getIsRequired(): boolean;
  protected abstract isShowRequriedOnly(): boolean;

  protected hasValue(): boolean {
    return this.control.value ||
      this.control.value === 0;
  }

  protected onRequiredChanged() {
    this.updateRequiredClasses();
    if (this.control) {
      this.control.updateValueAndValidity();
    }
  }

  protected onValueChanged() {
    this.updateValidClasses();
  }

  private calculateElements() {
    const element = this.elementRef.nativeElement as Element;
    const parentElement = element.parentElement as Element;
    const parentElementClass = parentElement.getAttribute('class');
    const inputGroupElement = parentElementClass && parentElementClass.indexOf('input-group') !== -1 ?
      parentElement :
      parentElement.querySelector('.input-group');
    this.element = inputGroupElement || element;

    this.formGroupElement = this.element.closest('.form-group');

    if (this.formGroupElement) {
      this.label = this.formGroupElement.getElementsByTagName('label')[0];
    }

    const document = this.browserService.getDocument();

    this.injectedDiv = document.createElement('div');
    this.injectedDiv.className = 'incorrect-input';
    this.injectedDiv.setAttribute('hidden', '');
    const innerDiv = document.createElement('div');
    innerDiv.setAttribute('hidden', '');
    innerDiv.setAttribute('id', `${this.name}-error-text-required`);

    let textNode;
    if (this.label && this.label.textContent) {
      textNode = document.createTextNode(this.label.textContent + ' is a required field');
    } else {
      textNode = document.createTextNode('Required field');
    }

    innerDiv.appendChild(textNode);
    this.injectedDiv.appendChild(innerDiv);

    if (this.formGroupElement) {
      this.formGroupElement.appendChild(this.injectedDiv);
    }

    this.formGroupInputElement =
      (this.name && document.querySelector('select[name="' + this.name + '"]')) ||
      (this.name && document.querySelector('input[name="' + this.name + '"]')) ||
      (this.name && document.querySelector('textarea[name="' + this.name + '"]')) || null;
  }

  private getIsValid() {
    const isValid = !this.getIsRequired() || this.hasValue() || this.isShowRequriedOnly();
    return isValid;
  }

  private initialize(control: AbstractControl) {
    this.control = control;
    this.calculateElements();
    this.updateRequiredClasses();
    this.updateValidClasses();

    control.valueChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      () => this.onValueChanged(),
    );

    this.ngForm.ngSubmit.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      () => this.onFormSubmitted(),
    );
  }

  private onFormSubmitted() {
    this.updateValidClasses();
  }

  private updateRequiredClasses() {
    if (this.formGroupInputElement) {
      const formGroupLabelClass = 'ap-form-group-required-new';
      if (this.getIsRequired()) {
        this.renderer.addClass(this.formGroupInputElement, formGroupLabelClass);
      } else {
        this.renderer.removeClass(this.formGroupInputElement, formGroupLabelClass);
      }
    }
  }

  private updateValidClasses() {
    const isValid = this.control.valid;
    const isFieldValid = isValid ||
      this.control.pristine && !this.ngForm.submitted;

    const fieldClassToAdd = isFieldValid ? 'ap-field-valid' : 'ap-field-invalid';
    const fieldClassToRemove = !isFieldValid ? 'ap-field-valid' : 'ap-field-invalid';
    this.renderer.addClass(this.element, fieldClassToAdd);
    this.renderer.removeClass(this.element, fieldClassToRemove);

    if (this.formGroupInputElement && this.ngForm.submitted) {
      if (this.getIsRequired() && !this.hasValue() && !this.isShowRequriedOnly()) {
        this.renderer.addClass(this.formGroupInputElement, 'ap-field-invalid-submitted');
      } else {
        this.renderer.removeClass(this.formGroupInputElement, 'ap-field-invalid-submitted');
      }
    } else if (this.formGroupElement) {
      const formGroupClassToAdd = isFieldValid ? 'ap-form-group-valid' : 'ap-form-group-invalid';
      const formGroupClassToRemove = !isFieldValid ? 'ap-form-group-valid' : 'ap-form-group-invalid';
      this.renderer.addClass(this.formGroupElement, formGroupClassToAdd);
      this.renderer.removeClass(this.formGroupElement, formGroupClassToRemove);
    }

    if (!this.injectedDiv) return;
    const injectedDivInnerDiv = this.injectedDiv.getElementsByTagName('div')[0];
    if (!injectedDivInnerDiv) return;

    if (this.formGroupInputElement) {
      if (this.formGroupInputElement.classList.contains('ap-field-invalid-submitted')) {
        this.injectedDiv.removeAttribute('hidden');
        injectedDivInnerDiv.removeAttribute('hidden');
      } else {
        this.injectedDiv.setAttribute('hidden', '');
        injectedDivInnerDiv.setAttribute('hidden', '');
      }
    }
  }
}
